// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * pieces.ts — the piece set and its rotations.
 *
 * The seven free tetrominoes (a mathematical set, not anyone's property) plus
 * one piece the classic set does not have: the INGOT, a single cell of mass 6.
 * It fills any hole and wrecks your trim, which is the whole tension of this
 * game compressed into one piece.
 *
 * Rotations are GENERATED from a base matrix rather than hand-typed, because a
 * hand-typed rotation table is four times the surface area for an off-by-one
 * that only shows up on one orientation of one piece.
 */

export type Cell = [number, number];

/** Cell type ids stored in the board. 0 is empty. */
export const EMPTY = 0;
export const T_I = 1;
export const T_O = 2;
export const T_T = 3;
export const T_S = 4;
export const T_Z = 5;
export const T_J = 6;
export const T_L = 7;
export const T_INGOT = 8;
export const T_CARGO = 9;
export const T_BALLAST = 10;

export const CELL_TYPES = [T_I, T_O, T_T, T_S, T_Z, T_J, T_L, T_INGOT, T_CARGO, T_BALLAST] as const;

/**
 * How much each cell weighs when the well works out how it hangs.
 *
 * This is the ONLY place mass is defined. The mechanism audit re-derives the
 * list from its own copy of these numbers rather than importing this table, so
 * a change here has to be a deliberate one in two places.
 */
export const MASS: Record<number, number> = {
  [T_I]: 1,
  [T_O]: 1,
  [T_T]: 1,
  [T_S]: 1,
  [T_Z]: 1,
  [T_J]: 1,
  [T_L]: 1,
  [T_INGOT]: 6,
  [T_CARGO]: 3,
  [T_BALLAST]: 2,
};

export function massOf(type: number): number {
  return MASS[type] ?? 0;
}

interface PieceDef {
  type: number;
  /** Side of the square matrix the base shape is drawn on. */
  n: number;
  base: Cell[];
}

const DEFS: PieceDef[] = [
  { type: T_I, n: 4, base: [[0, 1], [1, 1], [2, 1], [3, 1]] },
  { type: T_O, n: 2, base: [[0, 0], [1, 0], [0, 1], [1, 1]] },
  { type: T_T, n: 3, base: [[1, 0], [0, 1], [1, 1], [2, 1]] },
  { type: T_S, n: 3, base: [[1, 0], [2, 0], [0, 1], [1, 1]] },
  { type: T_Z, n: 3, base: [[0, 0], [1, 0], [1, 1], [2, 1]] },
  { type: T_J, n: 3, base: [[0, 0], [0, 1], [1, 1], [2, 1]] },
  { type: T_L, n: 3, base: [[2, 0], [0, 1], [1, 1], [2, 1]] },
  { type: T_INGOT, n: 1, base: [[0, 0]] },
];

/** Rotate a cell 90° clockwise inside an n×n matrix. */
function rotCw([x, y]: Cell, n: number): Cell {
  return [n - 1 - y, x];
}

function sortCells(cells: Cell[]): Cell[] {
  return cells.slice().sort((a, b) => (a[1] - b[1]) || (a[0] - b[0]));
}

export interface Piece {
  type: number;
  n: number;
  /** Four rotation states, index 0..3. States may repeat (O, INGOT). */
  rotations: Cell[][];
}

export const PIECES: Piece[] = DEFS.map((def) => {
  const rotations: Cell[][] = [];
  let cur = def.base;
  for (let r = 0; r < 4; r++) {
    rotations.push(sortCells(cur));
    cur = cur.map((c) => rotCw(c, def.n));
  }
  return { type: def.type, n: def.n, rotations };
});

const BY_TYPE = new Map<number, Piece>(PIECES.map((p) => [p.type, p]));

export function pieceOf(type: number): Piece {
  const p = BY_TYPE.get(type);
  if (!p) throw new Error(`unknown piece type ${type}`);
  return p;
}

/** The cells of `type` at rotation `rot`, offset to (x, y). */
export function cellsAt(type: number, rot: number, x: number, y: number): Cell[] {
  const p = pieceOf(type);
  const r = p.rotations[((rot % 4) + 4) % 4];
  return r.map(([cx, cy]) => [cx + x, cy + y] as Cell);
}

/** The seven bag pieces. The ingot is injected separately, not bagged. */
export const BAG_TYPES = [T_I, T_O, T_T, T_S, T_Z, T_J, T_L];

/**
 * Wall kicks. Deliberately simple and readable rather than a published system:
 * try in place, then one column each way, then two, then a floor kick, then a
 * CEILING kick. Documented because a kick table is exactly the sort of thing
 * that gets silently "improved" into something that plays differently.
 *
 * The downward entries ([0,1] and friends) are not decoration. A piece spawns
 * with its topmost cell on row 0, so standing the I-piece up needs a cell ABOVE
 * the ceiling — without a down-kick every kick still leaves row -1 occupied and
 * the rotation is refused. The result was that the I could not be turned
 * vertical on the move it spawned: the single most important rotation in the
 * game, silently dead. Found by playing it, pinned by tests/game.test.ts.
 */
export const KICKS: Cell[] = [
  [0, 0],
  [-1, 0],
  [1, 0],
  [-2, 0],
  [2, 0],
  [0, -1],
  [-1, -1],
  [1, -1],
  [0, 1],
  [-1, 1],
  [1, 1],
  [0, 2],
];
